import { useEffect, useState, ReactElement } from "react";

import styled from "styled-components";

import { PriceLabel } from "components/PriceLabel/PriceLabel";
import { Switch } from "components/Switch/Switch";
import { PreparedRouteApproval } from "containers/Applications/Applications";
import { showCommentInHTML } from "helpers/comment";
import { RESPONSIVE } from "helpers/responsive";
import * as Layout from "molecules/Layout";
import * as Table from "molecules/Table";

const RouteApplicationsTableActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const RouteApplicationsTableThActions = styled(Table.Th)`
  width: 110px;

  @media ${RESPONSIVE.DESKTOP} {
    width: 170px;
  }
`;

const RouteApplicationsTableThComments = styled(Table.Th)`
  @media ${RESPONSIVE.MOBILE} {
    display: none;
  }
`;

const RouteApplicationsTableTdComments = styled(Table.Td)`
  @media ${RESPONSIVE.MOBILE} {
    display: none;
  }
`;

const filterApprovalStatus = (
  preparedRouteApprovals: PreparedRouteApproval[],
  status: string[]
) =>
  preparedRouteApprovals.filter(
    (approval) =>
      status.filter((state) => approval.approvalStatus === state).length > 0
  );
const filterEmployeeId = (
  preparedRouteApprovals: PreparedRouteApproval[],
  employeeId: string
) =>
  preparedRouteApprovals.filter((approval) =>
    approval.preparedRoute.user
      ? approval.preparedRoute.user.employeeId.indexOf(employeeId) >= 0
      : false
  );

const renderPreparedRouteApprovals = (
  preparedRouteApproval: PreparedRouteApproval,
  toggleRouteApprovalDialogToApprove: (
    selectedPreparedRouteApproval: PreparedRouteApproval
  ) => void,
  toggleRouteApprovalDialogToDeny: (
    selectedPreparedRouteApproval: PreparedRouteApproval
  ) => void
) => {
  const { preparedRoute } = preparedRouteApproval;
  const { user } = preparedRoute;
  if (!user) return <tr key={preparedRouteApproval.id} />;

  return (
    <tr key={preparedRouteApproval.id}>
      <Table.Td>
        <a
          href={`/approver/users/${user.id}/prepared_routes/${preparedRoute.id}`}
        >
          {preparedRoute.id}
        </a>
      </Table.Td>
      <Table.Td>
        <span>{user.nameJp}</span>
        <br />
        <span>({user.employeeId})</span>
      </Table.Td>
      <Table.Td>{preparedRouteApproval.approvalStatusName}</Table.Td>
      <RouteApplicationsTableTdComments>
        {showCommentInHTML(preparedRouteApproval.approverComment)}
      </RouteApplicationsTableTdComments>
      <RouteApplicationsTableTdComments>
        {showCommentInHTML(preparedRouteApproval.applicantComment)}
      </RouteApplicationsTableTdComments>
      <Table.Td>{preparedRoute.arrivalName}</Table.Td>
      <Table.Td>
        <PriceLabel amount={preparedRoute.totalFare} />
      </Table.Td>
      <Table.Td>
        {preparedRouteApproval.approvalStatus === "applying" && (
          <div className="buttons has-addons">
            <button
              type="button"
              className="button"
              onClick={(_event) =>
                toggleRouteApprovalDialogToDeny(preparedRouteApproval)
              }
            >
              否認
            </button>
            <button
              type="button"
              className="button is-primary"
              onClick={(_event) =>
                toggleRouteApprovalDialogToApprove(preparedRouteApproval)
              }
            >
              承認
            </button>
          </div>
        )}
      </Table.Td>
    </tr>
  );
};

const hasApplying = (preparedRouteApprovals: PreparedRouteApproval[]) =>
  filterApprovalStatus(preparedRouteApprovals, ["applying"]).length > 0;

export interface RouteApplicationsTableProps {
  preparedRouteApprovals: PreparedRouteApproval[];
  toggleBulkApprovalDialog: () => void;
  toggleRouteApprovalDialogToApprove: (
    selectedPreparedRouteApproval: PreparedRouteApproval
  ) => void;
  toggleRouteApprovalDialogToDeny: (
    selectedPreparedRouteApproval: PreparedRouteApproval
  ) => void;
}

export function RouteApplicationsTable({
  preparedRouteApprovals,
  toggleBulkApprovalDialog,
  toggleRouteApprovalDialogToApprove,
  toggleRouteApprovalDialogToDeny,
}: RouteApplicationsTableProps): ReactElement | null {
  const [employeeId, setEmployeeId] = useState("");
  const [filtered, setFiltered] = useState(preparedRouteApprovals);
  const [isShowApproved, setShowApproved] = useState(false);

  const inputEmployeeId = (employeeId_: string) => {
    setEmployeeId(employeeId_);

    let data = preparedRouteApprovals;
    if (!isShowApproved) {
      data = filterApprovalStatus(data, ["unapplied", "applying"]);
    }

    setFiltered(filterEmployeeId(data, employeeId_));
  };

  const showApproved = () => {
    setShowApproved(!isShowApproved);

    let data = preparedRouteApprovals;
    if (employeeId) {
      data = filterEmployeeId(data, employeeId);
    }
    if (isShowApproved) {
      data = filterApprovalStatus(data, ["unapplied", "applying"]);
    }
    setFiltered(data);
  };

  useEffect(() => {
    if (preparedRouteApprovals) {
      setFiltered(
        filterApprovalStatus(preparedRouteApprovals, ["unapplied", "applying"])
      );
    }
    setShowApproved(false);
    setEmployeeId("");
  }, [preparedRouteApprovals]);

  return (
    <>
      <Layout.FlexWrapper alignItems="flex-start">
        <div />
        <Switch
          id="showPreparedRouteApproved"
          name="showPreparedRouteApproved"
          value={isShowApproved}
          rtl
          onChange={showApproved}
        >
          承認済みを表示
        </Switch>
      </Layout.FlexWrapper>
      <Table.Table>
        <thead>
          <tr>
            <Table.ThSmall>申請ID</Table.ThSmall>
            <Table.ThMedium>
              申請者
              <br />
              <input
                className="input"
                type="text"
                value={employeeId}
                placeholder="社員番号"
                onChange={(event) => inputEmployeeId(event.target.value)}
              />
            </Table.ThMedium>
            <Table.ThSmall>申請ステータス</Table.ThSmall>
            <RouteApplicationsTableThComments>
              あなたのコメント
            </RouteApplicationsTableThComments>
            <RouteApplicationsTableThComments>
              申請者のコメント
            </RouteApplicationsTableThComments>
            <Table.ThMedium>到着地</Table.ThMedium>
            <Table.ThSmall>金額</Table.ThSmall>
            <RouteApplicationsTableThActions>
              アクション
            </RouteApplicationsTableThActions>
          </tr>
        </thead>
        <tbody>
          {filtered.map((preparedRouteApproval) =>
            renderPreparedRouteApprovals(
              preparedRouteApproval,
              toggleRouteApprovalDialogToApprove,
              toggleRouteApprovalDialogToDeny
            )
          )}
          {preparedRouteApprovals.length <= 0 && (
            <tr>
              <td colSpan={8}>承認待ち申請はありません</td>
            </tr>
          )}
        </tbody>
      </Table.Table>
      <RouteApplicationsTableActions>
        <button
          type="button"
          disabled={!hasApplying(preparedRouteApprovals)}
          className="button is-primary"
          onClick={(_event) => toggleBulkApprovalDialog()}
        >
          一括承認
        </button>
      </RouteApplicationsTableActions>
    </>
  );
}
